/**
 * Data access for the Software tab on the mission page.
 *
 * The backend serves tools from a locally cached HSSI index, so the request
 * is cheap. The router loader on the mission page re-runs on every
 * pagination or facet change inside the same mission; the module-level cache
 * below keeps that from re-fetching the software list each time.
 */
import { useEffect, useState } from "react";
import axios from "axios";

export const HSSI_HOME_URL = "https://hssi.hsdcloud.org/";

const EMPTY = { tools: [], missionName: null };

// missionId -> Promise resolving to the normalised payload
const cache = new Map();

/** Reset the cache. Only tests should need this. */
export function clearMissionSoftwareCache() {
  cache.clear();
}

/**
 * GET the software list for one mission.
 *
 * @param missionId hcut_id of the mission, as used in page URLs.
 * @return Promise of the raw backend payload.
 */
export async function fetchMissionSoftware(missionId) {
  const response = await axios.get(
    `/heliodata/app/load_mission_software/${encodeURIComponent(missionId)}`
  );
  return response.data;
}

function normalise(data) {
  return {
    tools: Array.isArray(data?.tools) ? data.tools : [],
    missionName: data?.missionName || null,
  };
}

function load(missionId) {
  if (!cache.has(missionId)) {
    const promise = fetchMissionSoftware(missionId).then(normalise);
    // Drop a failed request so the next mount can retry
    promise.catch(() => cache.delete(missionId));
    cache.set(missionId, promise);
  }
  return cache.get(missionId);
}

/**
 * React hook: software tools for a mission.
 *
 * @param missionId hcut_id of the mission.
 * @return {status, tools, missionName} where status is
 *   "loading" | "ready" | "error".
 */
export function useMissionSoftware(missionId) {
  const [state, setState] = useState({ status: "loading", ...EMPTY });

  useEffect(() => {
    let active = true;
    setState({ status: "loading", ...EMPTY });
    load(missionId).then(
      (data) => { if (active) setState({ status: "ready", ...data }); },
      () => { if (active) setState({ status: "error", ...EMPTY }); }
    );
    return () => { active = false; };
  }, [missionId]);

  return state;
}

/**
 * Sentence for a tool that reached the mission through its instruments.
 *
 * @param names Instrument short names, already sorted by the backend.
 * @return "" | "Linked through A" | "Linked through A and B" | "Linked through A, B and N more"
 */
export function formatLinkedThrough(names) {
  if (!names || names.length === 0) {
    return "";
  }
  if (names.length <= 2) {
    return `Linked through ${names.join(" and ")}`;
  }
  return `Linked through ${names[0]}, ${names[1]} and ${names.length - 2} more`;
}
